import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/**
 * Caminho de gravação da TE.
 *
 * Como a FIV, o RPC recebe o estado inteiro e reconcilia numa transação.
 * As duas gravações tocam a tabela `embryos`, então precisam rodar em série —
 * em paralelo o Postgres trava uma na outra.
 */
export const saveTeSession = queueable('te.save',
  (params) => run(supabase.rpc('save_te_session', params)),
  { pendingOf: (params, appData) => appData && {
      kind: 'te', id: params.p_service_id, data: appData, label: 'Sessão de TE',
    } });

/** Sessão de TE completa de um serviço, ou null se ainda não existe. */
export const fetchTeSession = (serviceId) =>
  cached(`te-session:${serviceId}`, () => run(supabase
    .from('te_sessions')
    .select('*')
    .eq('service_id', serviceId)
    .maybeSingle()));

/** Transferências da sessão, na ordem em que foram lançadas. */
export const fetchTeTransfers = (sessionId) =>
  cached(`te-transfers:${sessionId}`, () => run(supabase
    .from('te_transfers')
    .select('*')
    .eq('te_session_id', sessionId)
    .order('sort_order')));

/**
 * Versão enxuta, para quem só precisa da data da TE.
 *
 * Tolerante a falha, mas a tolerância fica por FORA do cache de propósito: se
 * a consulta engolisse o erro por dentro, `cached` veria sucesso com resultado
 * vazio e nunca ofereceria a cópia local.
 */
export const fetchTeSessionRef = async (serviceId) => {
  try {
    return await cached(`te-ref:${serviceId}`, () => run(supabase
      .from('te_sessions')
      .select('id, session_date')
      .eq('service_id', serviceId)
      .maybeSingle()));
  } catch (err) {
    console.warn('[data] sessão de TE indisponível:', err.message);
    return null;
  }
};

/**
 * Transferências de um serviço com as checagens de DG que apontam para elas,
 * para a lista de embriões transferidos no PDF da fatura. Sem cache: o papel
 * sai com o que está gravado agora.
 */
export const transferenciasComDiagnostico = async (serviceId) => {
  const sessao = await run(supabase.from('te_sessions').select('id')
    .eq('service_id', serviceId).maybeSingle());
  if (!sessao) return { transferencias: [], checagens: [] };

  const [transferencias, sessoesDG] = await Promise.all([
    run(supabase.from('te_transfers')
      .select('id, status, embryo_number, donor_name, bull_name, recipient_identifier, '
        + 'embryo:embryo_id(service_number, bull_name, donor_identifier, donor:donor_id(identifier))')
      .eq('te_session_id', sessao.id)
      .order('sort_order')),
    run(supabase.from('dg_sessions').select('id, check_type').eq('service_id', serviceId)),
  ]);

  const tipoDaSessao = new Map((sessoesDG || []).map(s => [s.id, s.check_type]));
  const checagens = tipoDaSessao.size === 0 ? [] : await run(supabase.from('dg_checks')
    .select('te_transfer_id, dg_session_id, result')
    .in('dg_session_id', [...tipoDaSessao.keys()]));

  return {
    transferencias: transferencias || [],
    checagens: (checagens || []).map(c => ({ ...c, check_type: tipoDaSessao.get(c.dg_session_id) })),
  };
};

/**
 * A TE como está gravada AGORA: data e transferências aprovadas, para a DG
 * se acertar com ela. Sem sessão, null.
 *
 * Ao contrário de `fetchApprovedTransfers`, a falha SOBE: quem chama apaga
 * linha da DG que não está na lista, e uma lista vazia por falta de sinal
 * apagaria todas.
 */
export const teAgora = async (serviceId) => {
  const sessao = await run(supabase.from('te_sessions').select('id, session_date')
    .eq('service_id', serviceId).maybeSingle());
  if (!sessao) return null;
  const transfers = await run(supabase.from('te_transfers').select('*')
    .eq('te_session_id', sessao.id).eq('status', 'APROVADA').order('sort_order'));
  return { teDate: sessao.session_date, transfers: transfers || [] };
};

/** Transferências aprovadas de uma sessão, que viram as checagens de DG30. */
export const fetchApprovedTransfers = async (sessionId) => {
  try {
    return await cached(`te-aprovadas:${sessionId}`, () => run(supabase
      .from('te_transfers')
      .select('*')
      .eq('te_session_id', sessionId)
      .eq('status', 'APROVADA')
      .order('sort_order')));
  } catch (err) {
    console.warn('[data] transferências aprovadas indisponíveis:', err.message);
    return [];
  }
};
